import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ApiClientScalars } from "../../../../../../shared/index";
import { AUTH_TYPE_LABELS } from "../../../../../../shared/index";
import { OverviewTab } from "./tabs/overview";
import { AccessTab } from "./tabs/access";
import { KeysTab } from "./tabs/keys";
import { UsageTab } from "./tabs/usage";
import { HistoryTab } from "./tabs/history";
import { SaveDiffDialog, type PendingChange } from "../components/save-diff-dialog";

const TABS = ["overview", "access", "keys", "usage", "history"] as const;
type Tab = (typeof TABS)[number];

/**
 * One client, one page, one Save.
 *
 * The legacy drawer had four independent save buttons (scalars, feature URLs, news
 * topics, countries), each firing its own request behind its own `confirm()`, so a
 * half-applied client was one failed request away. Here the pending edits accumulate,
 * Save shows exactly what will change, and the write is a single transaction.
 */
export default function B2bClientDetail() {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [pending, setPending] = useState<PendingChange | null>(null);

  const detail = useQuery({
    queryKey: ["b2b", "client", clientId],
    queryFn: () => api.b2b.getClient(clientId),
    enabled: Number.isInteger(clientId),
  });

  const catalog = useQuery({ queryKey: ["b2b", "catalog"], queryFn: api.b2b.catalog });
  const lookups = useQuery({ queryKey: ["b2b", "lookups"], queryFn: api.b2b.lookups });

  // Draft edits, applied only on Save.
  const [scalarDraft, setScalarDraft] = useState<Partial<ApiClientScalars>>({});
  const [accessDraft, setAccessDraft] = useState<{
    featureUrlIds: number[];
    flags: { featureId: number; enabled: boolean; customValue: string | null }[];
    countryIds: number[];
    newsCategoryIds: number[];
  } | null>(null);

  const client = detail.data?.client;

  useEffect(() => {
    // A fresh load discards drafts, so a background refetch can never silently merge
    // someone else's change into this operator's pending edit.
    setScalarDraft({});
    setAccessDraft(null);
  }, [clientId]);

  const dirty = Object.keys(scalarDraft).length > 0 || accessDraft !== null;

  const save = useMutation({
    mutationFn: async () => {
      if (Object.keys(scalarDraft).length > 0) {
        await api.b2b.updateClient(clientId, scalarDraft);
      }
      if (accessDraft) {
        await api.b2b.setAccess(clientId, accessDraft);
      }
    },
    onSuccess: async () => {
      setScalarDraft({});
      setAccessDraft(null);
      setPending(null);
      await queryClient.invalidateQueries({ queryKey: ["b2b"] });
      toast.success("Saved. The API caches were evicted on every runtime.");
    },
    onError: (error) => {
      setPending(null);
      toast.error(error instanceof ApiError ? error.message : "Save failed");
    },
  });

  const diffPreview = useMemo(() => {
    if (!client) return [];
    const rows: { path: string; before: unknown; after: unknown }[] = [];

    for (const [key, value] of Object.entries(scalarDraft)) {
      rows.push({
        path: key,
        before: (client as unknown as Record<string, unknown>)[key],
        after: value,
      });
    }

    if (accessDraft && catalog.data) {
      const nameById = new Map(catalog.data.map((e) => [e.featureUrlId, e.featureUrl]));
      const currentIds = new Set(
        client.grants.filter((g) => g.featureUrlId !== null).map((g) => g.featureUrlId!)
      );
      const nextIds = new Set(accessDraft.featureUrlIds);
      const added = [...nextIds].filter((i) => !currentIds.has(i)).map((i) => nameById.get(i) ?? i);
      const removed = [...currentIds].filter((i) => !nextIds.has(i)).map((i) => nameById.get(i) ?? i);
      if (added.length > 0 || removed.length > 0) {
        rows.push({ path: "grants", before: removed, after: added });
      }

      const flagBefore = client.featureFlags.filter((f) => f.enabled).map((f) => f.name);
      const featureNameById = new Map(
        (lookups.data?.features ?? []).map((f) => [f.featureId, f.name])
      );
      const flagAfter = accessDraft.flags
        .filter((f) => f.enabled)
        .map((f) => featureNameById.get(f.featureId) ?? String(f.featureId));
      if (flagBefore.sort().join() !== flagAfter.slice().sort().join()) {
        rows.push({ path: "featureFlags", before: flagBefore, after: flagAfter });
      }
    }

    return rows;
  }, [client, scalarDraft, accessDraft, catalog.data, lookups.data]);

  if (detail.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (detail.isError || !client) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">Client not found.</p>
        <Button variant="secondary" onClick={() => navigate("/dashboard/b2b/clients")}>
          Back to clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/dashboard/b2b/clients"
          className="inline-flex items-center text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="mr-1 h-3.5 w-3.5" />
          All clients
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">{client.key}</h1>
          <Badge variant="secondary">{AUTH_TYPE_LABELS[client.authType]}</Badge>
          {!client.isActive && <Badge variant="secondary">inactive</Badge>}
          {client.authType === "singleFeature" && client.grants.length === 0 && (
            <Badge variant="destructive">no grants — every call returns 401</Badge>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-b">
        <nav className="flex gap-1">
          {TABS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setTab(name)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm capitalize ${
                tab === name
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {name}
            </button>
          ))}
        </nav>
        {dirty && (
          <Button onClick={() => setPending({ diff: diffPreview })} className="mb-2">
            Save changes
          </Button>
        )}
      </div>

      {tab === "overview" && (
        <OverviewTab
          client={client}
          draft={scalarDraft}
          onChange={(patch) => setScalarDraft((prev) => ({ ...prev, ...patch }))}
        />
      )}
      {tab === "access" && (
        <AccessTab
          client={client}
          effectiveAccess={detail.data!.effectiveAccess}
          catalog={catalog.data ?? []}
          features={lookups.data?.features ?? []}
          draft={accessDraft}
          onChange={setAccessDraft}
        />
      )}
      {tab === "keys" && <KeysTab client={client} />}
      {tab === "usage" && <UsageTab clientKey={client.key} />}
      {tab === "history" && <HistoryTab clientId={clientId} />}

      <SaveDiffDialog
        pending={pending}
        saving={save.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => save.mutate()}
      />
    </div>
  );
}
